import { Event, EventType } from '@frontend/events/types';
import React from 'react';
import { LuCircleArrowDown, LuCircleArrowRight, LuCircleArrowUp, LuCircleCheck, LuCircleX, LuMail } from 'react-icons/lu';

import { isErrorHistoryEvent } from './map-event-data-to-display';

export interface HistoryEventIconProps {
    event: Event;
    isLast?: boolean;
    isFirst?: boolean;
}

export const HistoryEventIcon = (props: HistoryEventIconProps) => {
    switch (props.event.type) {
        case EventType.TRANSACTION_STATE_CHANGED:
        case EventType.COLLI_STATE_CHANGED:
        case EventType.PACKAGE_STATE_CHANGED:
            return (
                <StateChangedIcon
                    event={props.event}
                    isLast={props.isLast}
                    isFirst={props.isFirst}
                />
            );
        case EventType.TRANSACTION_HISTORY_LOGGED:
        case EventType.COLLI_HISTORY_LOGGED:
        case EventType.USER_HISTORY_LOGGED:
            return (
                <HistoryLoggedIcon
                    event={props.event}
                    isLast={props.isLast}
                    isFirst={props.isFirst}
                />
            );
        case EventType.NOTIFICATION_SENT:
            return (
                <NotificationSentIcon
                    event={props.event}
                    isLast={props.isLast}
                    isFirst={props.isFirst}
                />
            );
        default:
            return (
                <HistoryEventIconWrapper isLast={props.isLast}>
                    <div className='flex size-5 items-center justify-center'>
                        <span className={`size-2 rounded-full ${props.isFirst ? 'bg-status-success' : 'bg-brand-secondary'}`} />
                    </div>
                </HistoryEventIconWrapper>
            );
    }
};

const HistoryEventIconWrapper = (props: { isLast?: boolean; children: React.ReactNode }) => (
    <div className='relative w-5 shrink-0'>
        <div className={`bg-brand-secondary absolute top-5 left-1/2 w-0.5 -translate-x-1/2 ${props.isLast === false ? 'bottom-0' : 'bottom-4'}`} />
        {props.children}
    </div>
);

export const NotificationSentIcon = (props: { event: Event; isLast?: boolean; isFirst?: boolean }) => {
    return (
        <HistoryEventIconWrapper isLast={props.isLast}>
            <LuMail className='text-status-info size-5' />
        </HistoryEventIconWrapper>
    );
};

const DefaultHistoryDot = (props: { isLast?: boolean; isFirst?: boolean }) => (
    <HistoryEventIconWrapper isLast={props.isLast}>
        <div className='flex size-5 items-center justify-center'>
            <span className={`size-2 rounded-full ${props.isFirst ? 'bg-status-success' : 'bg-brand-secondary'}`} />
        </div>
    </HistoryEventIconWrapper>
);

const ErrorHistoryIcon = (props: { isLast?: boolean }) => (
    <HistoryEventIconWrapper isLast={props.isLast}>
        <LuCircleX className='text-status-danger size-5' />
    </HistoryEventIconWrapper>
);

export const HistoryLoggedIcon = (props: { event: Event; isLast?: boolean; isFirst?: boolean }) => {
    if (isErrorHistoryEvent(props.event)) {
        return <ErrorHistoryIcon isLast={props.isLast} />;
    }

    switch (props.event.data) {
        case 'transaction_log_completed':
        case 'colli_log_completed':
        case 'package_log_completed':
            return (
                <HistoryEventIconWrapper isLast={props.isLast}>
                    <LuCircleCheck className='text-status-success size-5' />
                </HistoryEventIconWrapper>
            );
        case 'transaction_log_picked_up':
        case 'colli_log_picked_up':
        case 'package_log_picked_up':
            return (
                <HistoryEventIconWrapper isLast={props.isLast}>
                    <LuCircleArrowUp className='text-brand-primary size-5' />
                </HistoryEventIconWrapper>
            );
        case 'transaction_log_dropped_off':
        case 'colli_log_dropped_off':
        case 'package_log_dropped_off':
            return (
                <HistoryEventIconWrapper isLast={props.isLast}>
                    <LuCircleArrowDown className='text-brand-secondary size-5' />
                </HistoryEventIconWrapper>
            );
        case 'transaction_log_created':
        case 'colli_log_created':
        case 'package_log_created':
        default:
            return (
                <DefaultHistoryDot
                    isLast={props.isLast}
                    isFirst={props.isFirst}
                />
            );
    }
};

export const StateChangedIcon = (props: { event: Event; isLast?: boolean; isFirst?: boolean }) => {
    switch (props.event.data) {
        case 'transaction_completed':
        case 'colli_completed':
            return (
                <HistoryEventIconWrapper isLast={props.isLast}>
                    <LuCircleCheck className='text-status-success size-5' />
                </HistoryEventIconWrapper>
            );
        case 'transaction_pick_up_pending':
        case 'transaction_pick_up_done':
        case 'colli_pick_up_pending':
        case 'colli_pick_up_done':
        case 'package_pick_up_pending':
            return (
                <HistoryEventIconWrapper isLast={props.isLast}>
                    <LuCircleArrowUp className='text-brand-primary size-5' />
                </HistoryEventIconWrapper>
            );
        case 'transaction_drop_off_pending':
        case 'colli_drop_off_pending':
        case 'package_drop_off_pending':
            return (
                <HistoryEventIconWrapper isLast={props.isLast}>
                    <LuCircleArrowDown className='text-brand-secondary size-5' />
                </HistoryEventIconWrapper>
            );
        case 'transaction_in_transit':
        case 'colli_in_transit':
            return (
                <HistoryEventIconWrapper isLast={props.isLast}>
                    <LuCircleArrowRight className='text-status-warning size-5' />
                </HistoryEventIconWrapper>
            );
        default:
            return (
                <DefaultHistoryDot
                    isLast={props.isLast}
                    isFirst={props.isFirst}
                />
            );
    }
};
